import { create } from 'zustand'
import { videosApi, linksApi } from '../services/api'
import type { Video, TrackingLink, CreateVideoFormData, CreateLinkFormData } from '../types'

interface VideoState {
  videos: Video[]
  currentVideo: Video | null
  links: TrackingLink[]
  isLoading: boolean
  error: string | null
  total: number
  page: number
  pages: number

  // Video actions
  fetchVideos: (params?: { page?: number; search?: string; status?: string }) => Promise<void>
  fetchVideo: (id: string) => Promise<void>
  createVideo: (data: CreateVideoFormData) => Promise<Video>
  updateVideo: (id: string, data: Partial<CreateVideoFormData>) => Promise<void>
  deleteVideo: (id: string) => Promise<void>

  // Link actions
  fetchLinks: (videoId: string) => Promise<void>
  createLink: (videoId: string, data: CreateLinkFormData) => Promise<TrackingLink>
  updateLink: (id: string, data: Partial<CreateLinkFormData>) => Promise<void>
  deleteLink: (id: string) => Promise<void>

  clearError: () => void
}

export const useVideoStore = create<VideoState>((set) => ({
  videos: [],
  currentVideo: null,
  links: [],
  isLoading: false,
  error: null,
  total: 0,
  page: 1,
  pages: 1,

  fetchVideos: async (params) => {
    set({ isLoading: true, error: null })
    try {
      const response = await videosApi.list(params)
      set({
        videos: response.videos,
        total: response.total,
        page: response.page,
        pages: response.pages,
        isLoading: false,
      })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch videos',
        isLoading: false,
      })
    }
  },

  fetchVideo: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const video = await videosApi.get(id, true)
      set({ currentVideo: video, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch video',
        isLoading: false,
      })
    }
  },

  createVideo: async (data: CreateVideoFormData) => {
    set({ isLoading: true, error: null })
    try {
      const video = await videosApi.create(data)
      set((state) => ({
        videos: [video, ...state.videos],
        isLoading: false,
      }))
      return video
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to create video',
        isLoading: false,
      })
      throw err
    }
  },

  updateVideo: async (id: string, data: Partial<CreateVideoFormData>) => {
    set({ isLoading: true, error: null })
    try {
      const video = await videosApi.update(id, data)
      set((state) => ({
        videos: state.videos.map((v) => (v.id === id ? video : v)),
        currentVideo: state.currentVideo?.id === id ? video : state.currentVideo,
        isLoading: false,
      }))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to update video',
        isLoading: false,
      })
      throw err
    }
  },

  deleteVideo: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await videosApi.delete(id)
      set((state) => ({
        videos: state.videos.filter((v) => v.id !== id),
        currentVideo: state.currentVideo?.id === id ? null : state.currentVideo,
        isLoading: false,
      }))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to delete video',
        isLoading: false,
      })
      throw err
    }
  },

  fetchLinks: async (videoId: string) => {
    set({ isLoading: true, error: null })
    try {
      const links = await videosApi.getLinks(videoId)
      set({ links, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch links',
        isLoading: false,
      })
    }
  },

  createLink: async (videoId: string, data: CreateLinkFormData) => {
    set({ isLoading: true, error: null })
    try {
      const link = await videosApi.createLink(videoId, data)
      set((state) => ({
        links: [link, ...state.links],
        isLoading: false,
      }))
      return link
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to create link',
        isLoading: false,
      })
      throw err
    }
  },

  updateLink: async (id: string, data: Partial<CreateLinkFormData>) => {
    set({ isLoading: true, error: null })
    try {
      const link = await linksApi.update(id, data)
      set((state) => ({
        links: state.links.map((l) => (l.id === id ? link : l)),
        isLoading: false,
      }))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to update link',
        isLoading: false,
      })
      throw err
    }
  },

  deleteLink: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await linksApi.delete(id)
      set((state) => ({
        links: state.links.filter((l) => l.id !== id),
        isLoading: false,
      }))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to delete link',
        isLoading: false,
      })
      throw err
    }
  },

  clearError: () => set({ error: null }),
}))
